/**
 * The node modal's `proposal` prop for a card on the v2 map, and what happens
 * to local state when the person has decided (specs/workshop-modules/
 * impact_path.md, "Proposed nodes").
 *
 * Returns a function of the live node: null for an ordinary card, otherwise
 * the props ProposedNodePanel needs. The panel does the request; the result
 * is applied here, optimistically for everyone on this device: a kept or
 * softened node is updated in place, a rejected one leaves the list and its
 * children move to its parent, as the server did.
 */
import { useCallback } from 'react';

export function useProposedNode({ instanceId, participantId, setPathways, onRejected }) {
  return useCallback((live) => {
    if (!live) return null;
    const status = live.status || 'active';
    const provenance = live.node_analysis?.provenance || null;
    if (status !== 'proposed' && !provenance) return null;
    return {
      nodeId: live.id,
      instanceId,
      participantId: participantId || 'facilitator',
      status,
      text: live.title || live.text || '',
      provenance,
      decision: live.node_analysis?.proposal || null,
      onDecided: ({ node, children }) => {
        if (!node) return;
        setPathways((prev) => {
          const moved = new Map((children || []).map((c) => [c.id, c]));
          const next = prev.map((n) => (moved.has(n.id) ? { ...n, ...moved.get(n.id) } : n));
          if (node.status === 'rejected') return next.filter((n) => n.id !== node.id);
          return next.map((n) => (n.id === node.id ? { ...n, ...node } : n));
        });
        if (node.status === 'rejected') onRejected?.();
      },
    };
  }, [instanceId, participantId, setPathways, onRejected]);
}
